import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  UserSession,
  SecurityEvent,
  UserActivity,
  SecuritySummary,
  SecurityDashboard,
  PaginatedResponse,
  ApiResponse,
  UserActivityQuery,
  SecurityEventsQuery
} from '../models/security.models';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Security Summary Dashboard
  getSecuritySummary(fromDate?: string, toDate?: string): Observable<SecuritySummary> {
    let params = new HttpParams();
    
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);

    return this.http.get<SecuritySummary>(`${this.apiUrl}/user/admin-only/security/summary`, { params });
  }

  // User Activity Monitoring
  getUserActivities(query?: {
    userId?: number;
    action?: string;
    resource?: string;
    fromDate?: string;
    toDate?: string;
    success?: boolean;
    page?: number;
    pageSize?: number;
  }): Observable<PaginatedResponse<UserActivity>> {
    let params = new HttpParams();
    
    if (query?.userId) params = params.set('userId', query.userId.toString());
    if (query?.action) params = params.set('action', query.action);
    if (query?.resource) params = params.set('resource', query.resource);
    if (query?.fromDate) params = params.set('fromDate', query.fromDate);
    if (query?.toDate) params = params.set('toDate', query.toDate);
    if (query?.success !== undefined) params = params.set('success', query.success.toString());
    if (query?.page) params = params.set('page', query.page.toString());
    if (query?.pageSize) params = params.set('pageSize', query.pageSize.toString());

    return this.http.get<PaginatedResponse<UserActivity>>(`${this.apiUrl}/user/admin-only/security/activities`, { params });
  }

  // Security Events Management
  getSecurityEvents(query?: {
    severity?: string;
    isResolved?: boolean;
    eventType?: string;
    fromDate?: string;
    toDate?: string;
    pageSize?: number;
  }): Observable<PaginatedResponse<SecurityEvent>> {
    let params = new HttpParams();
    
    if (query?.severity) params = params.set('severity', query.severity);
    if (query?.isResolved !== undefined) params = params.set('isResolved', query.isResolved.toString());
    if (query?.eventType) params = params.set('eventType', query.eventType);
    if (query?.fromDate) params = params.set('fromDate', query.fromDate);
    if (query?.toDate) params = params.set('toDate', query.toDate);
    if (query?.pageSize) params = params.set('pageSize', query.pageSize.toString());

    return this.http.get<PaginatedResponse<SecurityEvent>>(`${this.apiUrl}/user/admin-only/security/events`, { params });
  }

  resolveSecurityEvent(eventId: string): Observable<ApiResponse<string>> {
    return this.http.patch<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/security/events/${eventId}/resolve`, {});
  }

  // Active Sessions Management
  getActiveSessions(query?: {
    userId?: number;
    isActive?: boolean;
  }): Observable<UserSession[]> {
    let params = new HttpParams();
    
    if (query?.userId) params = params.set('userId', query.userId.toString());
    if (query?.isActive !== undefined) params = params.set('isActive', query.isActive.toString());

    return this.http.get<UserSession[]>(`${this.apiUrl}/user/admin-only/security/sessions`, { params });
  }

  invalidateSession(sessionId: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/security/sessions/${sessionId}/invalidate`, {});
  }

  // User Security Profile
  getUserSecurityProfile(userId: number): Observable<{
    user: any;
    securitySettings: any;
    recentActivities: UserActivity[];
    activeSessions: UserSession[];
  }> {
    return this.http.get<{
      user: any;
      securitySettings: any;
      recentActivities: UserActivity[];
      activeSessions: UserSession[];
    }>(`${this.apiUrl}/user/admin-only/users/${userId}/security-profile`);
  }

  // Account Security Management
  lockUserAccount(userId: number, reason: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/users/${userId}/lock`, { reason });
  }

  unlockUserAccount(userId: number): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/users/${userId}/unlock`, {});
  }

  // Invalidate all user sessions
  invalidateAllUserSessions(userId: number): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/users/${userId}/invalidate-sessions`, {});
  }

  // Session Management - Enhanced with device detection
  getUserSessionsWithDetails(userId: number): Observable<UserSession[]> {
    return this.http.get<UserSession[]>(`${this.apiUrl}/user/admin-only/users/${userId}/sessions`);
  }

  // Bulk session management
  invalidateMultipleSessions(sessionIds: string[]): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/security/sessions/bulk-invalidate`, {
      sessionIds
    });
  }

  // Session monitoring for suspicious activity
  flagSuspiciousSession(sessionId: string, reason: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/user/admin-only/security/sessions/${sessionId}/flag`, {
      reason
    });
  }

  // Real-time session monitoring
  getSessionMetrics(): Observable<{
    totalActiveSessions: number;
    uniqueActiveUsers: number;
    suspiciousSessions: number;
    peakSessionsToday: number;
    averageSessionDuration: number;
  }> {
    return this.http.get<any>(`${this.apiUrl}/user/admin-only/security/session-metrics`);
  }

  // Export functionality for compliance
  exportUserActivity(userId: number, fromDate?: string, toDate?: string): Observable<Blob> {
    let params = new HttpParams();
    
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);

    return this.http.get(`${this.apiUrl}/user/admin-only/users/${userId}/activity/export`, {
      params,
      responseType: 'blob'
    });
  }

  exportSecurityEvents(fromDate?: string, toDate?: string): Observable<Blob> {
    let params = new HttpParams();
    
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);

    return this.http.get(`${this.apiUrl}/user/admin-only/security/events/export`, {
      params,
      responseType: 'blob'
    });
  }
}
